import { AlertTriangle, Clock, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface Alert {
  id: string;
  type: 'warning' | 'danger' | 'info';
  title: string;
  message: string;
  count?: number;
  link?: string;
}

export interface AlertsBannerProps {
  lowStockCount: number;
  overdueInvoicesCount: number;
  loading?: boolean;
}

// ─────────────────────────────────────────────────────────────
// Alert Styles
// ─────────────────────────────────────────────────────────────

const alertStyles = {
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    title: 'text-amber-800',
    message: 'text-amber-700',
  },
  danger: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    title: 'text-red-800',
    message: 'text-red-700',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    title: 'text-blue-800',
    message: 'text-blue-700',
  },
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function AlertsBanner({
  lowStockCount,
  overdueInvoicesCount,
  loading = false,
}: AlertsBannerProps) {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  // Build alerts list based on data
  const alerts: Alert[] = [];

  if (lowStockCount > 0) {
    alerts.push({
      id: 'low-stock',
      type: 'warning',
      title: 'Stock bas',
      message: `${lowStockCount} article${lowStockCount > 1 ? 's' : ''} en stock bas`,
      count: lowStockCount,
      link: '/articles?filter=low-stock',
    });
  }

  if (overdueInvoicesCount > 0) {
    alerts.push({
      id: 'overdue-invoices',
      type: 'danger',
      title: 'Factures en retard',
      message: `${overdueInvoicesCount} facture${overdueInvoicesCount > 1 ? 's' : ''} en retard de paiement`,
      count: overdueInvoicesCount,
      link: '/invoices?filter=overdue',
    });
  }

  const visibleAlerts = alerts.filter((alert) => !dismissedAlerts.has(alert.id));

  const handleDismiss = (alertId: string) => {
    setDismissedAlerts((prev) => new Set([...prev, alertId]));
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-16 rounded-lg bg-slate-200" />
      </div>
    );
  }

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {visibleAlerts.map((alert) => {
        const styles = alertStyles[alert.type];
        const IconComponent = alert.type === 'danger' ? Clock : AlertTriangle;

        return (
          <div
            key={alert.id}
            className={cn(
              'relative flex items-center gap-4 rounded-lg border p-4',
              styles.bg,
              styles.border
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
                alert.type === 'warning' ? 'bg-amber-100' : alert.type === 'danger' ? 'bg-red-100' : 'bg-blue-100'
              )}
            >
              <IconComponent className={cn('h-5 w-5', styles.icon)} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={cn('text-sm font-semibold', styles.title)}>
                {alert.title}
              </h4>
              <p className={cn('text-sm', styles.message)}>{alert.message}</p>
            </div>
            {alert.link && (
              <a
                href={alert.link}
                className={cn(
                  'text-sm font-medium underline-offset-2 hover:underline',
                  styles.title
                )}
              >
                Voir
              </a>
            )}
            <button
              onClick={() => handleDismiss(alert.id)}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                'hover:bg-black/5'
              )}
              aria-label="Fermer l'alerte"
            >
              <X className={cn('h-4 w-4', styles.icon)} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default AlertsBanner;
